/** "Composition" card — the notification's block/list references (ordered,
 *  editable), the default-blocks toggle, and a resolved-namespace preview.
 */
import { FormControlLabel, Link, Paper, Stack, Switch, Tooltip, Typography } from '@mui/material';

import type { BlockRefItem, NotificationDetail } from '@/api/notifications';
import { FieldHelp, labelWithHelp } from '@/components/FieldHelp';

import { BlockRefsEditor } from './composition/BlockRefsEditor';
import { NOTIFICATION_HELP } from './fieldHelp';
import { ResolvedBlocksPreview } from './composition/ResolvedBlocksPreview';

/** Scroll target used by the delivery editors' "Blocks" provenance chip. */
export const COMPOSITION_PANEL_ID = 'nf-composition-panel';

interface Props {
  detail: NotificationDetail;
  blockRefs: BlockRefItem[];
  disableDefaultBlocks: boolean;
  onToggleDefaultBlocks: (disabled: boolean) => void;
  onBlockRefsChange: (refs: BlockRefItem[]) => void;
  /** Row errors from mapNotificationErrors().blockRefs. */
  errors: Record<string, string>;
  canManage: boolean;
  onOpenTestRender: () => void;
}

export function NotificationCompositionPanel({
  detail,
  blockRefs,
  disableDefaultBlocks,
  onToggleDefaultBlocks,
  onBlockRefsChange,
  errors,
  canManage,
  onOpenTestRender,
}: Props) {
  return (
    <Paper id={COMPOSITION_PANEL_ID} variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
            Composition <FieldHelp help={NOTIFICATION_HELP.composition} />
          </Typography>
          <Tooltip title="When on, only the block references below feed the block.* namespace — the tenant/system default block lists are skipped.">
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={disableDefaultBlocks}
                  disabled={!canManage}
                  onChange={(e) => onToggleDefaultBlocks(e.target.checked)}
                />
              }
              label={labelWithHelp(
                <Typography variant="body2">Disable default blocks</Typography>,
                NOTIFICATION_HELP.disableDefaultBlocks,
              )}
            />
          </Tooltip>
        </Stack>

        <BlockRefsEditor
          value={blockRefs}
          onChange={onBlockRefsChange}
          tenantId={detail.notification.tour_operator ?? 'system'}
          errors={errors}
          disabled={!canManage}
        />

        <ResolvedBlocksPreview blockRefs={blockRefs} disableDefaultBlocks={disableDefaultBlocks} />

        <Typography variant="caption" color="text.secondary">
          Blocks render inside the standard layout / custom templates —{' '}
          <Link
            component="button"
            type="button"
            variant="caption"
            underline="hover"
            onClick={onOpenTestRender}
          >
            verify with a test render →
          </Link>
        </Typography>
      </Stack>
    </Paper>
  );
}
